import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { describeDiscountValue } from '@mlm/shared';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@mlm/ui';
import {
  createCoupon,
  deleteCoupon,
  listCoupons,
  updateCoupon,
  type Coupon,
  type CouponChannel,
  type CouponInput,
} from '../lib/api';
import { useProductSearch } from '../lib/use-product-search';
import { useProductsByIds } from '../lib/use-products-by-ids';

/**
 * Discounts admin. Two kinds of the same thing:
 *
 *  - a TILL PRESET is a button the cashier presses at checkout ("Senior
 *    citizen 20%"). It has a name instead of a code, and only works at the till;
 *  - a CODE is typed by the customer at the online store, by the cashier at the
 *    till, or both, depending on where it is allowed.
 *
 * Both discount per line: every line of the order, or only the selected
 * products. Cash orders only, and PV is never reduced.
 */
type Kind = 'preset' | 'code';

const CHANNEL_LABEL: Record<CouponChannel, string> = {
  ecom: 'Online store',
  pos: 'Till',
  both: 'Online store and till',
};

export function CouponsPage() {
  const qc = useQueryClient();
  const couponsQuery = useQuery({ queryKey: ['admin-coupons'], queryFn: listCoupons });
  const [tab, setTab] = useState<Kind>('preset');
  const [creating, setCreating] = useState<Kind | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: deleteCoupon,
    meta: { successMessage: 'Discount deleted.' },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-coupons'] }),
  });

  const all = couponsQuery.data ?? [];
  // Presets in the order the till shows its buttons (by name); their codes are
  // generated, so the server's code order would look random here.
  const rows: Record<Kind, Coupon[]> = {
    preset: all
      .filter((c) => c.posPreset)
      .sort((a, b) => (a.name ?? a.code).localeCompare(b.name ?? b.code)),
    code: all.filter((c) => !c.posPreset),
  };
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin-coupons'] });

  function list(kind: Kind) {
    const items = rows[kind];
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {kind === 'preset'
            ? 'Buttons the cashier presses at checkout instead of typing a discount. A branch offers them when its cashier discount is set to presets (Branches, a branch, Discounts).'
            : 'Codes a customer types at the online store or hands over at the till. A new code works at the online store only, until you allow it at the till.'}
        </p>

        {creating === kind && (
          <CouponForm
            kind={kind}
            onClose={() => setCreating(null)}
            onSaved={() => {
              refresh();
              setCreating(null);
            }}
          />
        )}

        <Card>
          <CardHeader>
            <CardTitle>{kind === 'preset' ? 'Till presets' : 'Codes'}</CardTitle>
          </CardHeader>
          <CardContent>
            {couponsQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : items.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {kind === 'preset'
                  ? 'No presets yet. Add the first one with “New preset”.'
                  : 'No codes yet.'}
              </p>
            ) : (
              <ul className="divide-y">
                {items.map((c) =>
                  editing === c.id ? (
                    <li key={c.id} className="py-3">
                      <CouponForm
                        kind={kind}
                        coupon={c}
                        onClose={() => setEditing(null)}
                        onSaved={() => {
                          refresh();
                          setEditing(null);
                        }}
                      />
                    </li>
                  ) : (
                    <li key={c.id} className="flex items-center gap-3 py-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          {kind === 'preset' ? (
                            <span className="font-medium">{c.name ?? c.code}</span>
                          ) : (
                            <span className="font-mono font-medium">{c.code}</span>
                          )}
                          <Tag>{describeDiscountValue(c)}</Tag>
                          {kind === 'code' && <Tag>{CHANNEL_LABEL[c.channel]}</Tag>}
                          {c.requiresManagerPin && <Tag>Manager PIN</Tag>}
                          {!c.active && <Tag>Inactive</Tag>}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {kind === 'code' && c.name ? `${c.name} · ` : ''}
                          {c.appliesToAll ? 'Whole order' : `${c.productIds.length} product(s)`}
                          {' · '}
                          {c.oneTime
                            ? 'One-time use'
                            : c.maxUses != null
                              ? `Up to ${c.maxUses} uses`
                              : 'Unlimited uses'}
                          {' · '}
                          {c.usedCount} used
                          {c.perUserLimit != null && ` · ${c.perUserLimit}/member`}
                          {(c.validFrom || c.validUntil) &&
                            ` · ${c.validFrom ? new Date(c.validFrom).toLocaleDateString() : '…'}–${c.validUntil ? new Date(c.validUntil).toLocaleDateString() : '…'}`}
                        </div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setEditing(c.id)}>
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const label = kind === 'preset' ? (c.name ?? c.code) : c.code;
                          if (confirm(`Delete "${label}"?`)) remove.mutate(c.id);
                        }}
                      >
                        Delete
                      </Button>
                    </li>
                  ),
                )}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Discounts</h1>
        <Button onClick={() => setCreating(tab)}>
          {tab === 'preset' ? '+ New preset' : '+ New code'}
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        A discount applies per line, to the whole order or only to the products you select. Cash
        orders only, and PV is never reduced.
      </p>

      <Tabs
        value={tab}
        onValueChange={(v) => {
          setTab(v as Kind);
          setCreating(null);
          setEditing(null);
        }}
      >
        <TabsList>
          <TabsTrigger value="preset">Till presets</TabsTrigger>
          <TabsTrigger value="code">Codes</TabsTrigger>
        </TabsList>
        <TabsContent value="preset">{list('preset')}</TabsContent>
        <TabsContent value="code">{list('code')}</TabsContent>
      </Tabs>
    </div>
  );
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{children}</span>
  );
}

function CouponForm({
  kind,
  coupon,
  onClose,
  onSaved,
}: {
  kind: Kind;
  coupon?: Coupon;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isPreset = kind === 'preset';
  const [code, setCode] = useState(coupon?.code ?? '');
  const [name, setName] = useState(coupon?.name ?? '');
  const [channel, setChannel] = useState<CouponChannel>(coupon?.channel ?? 'ecom');
  const [discountType, setDiscountType] = useState<'percent' | 'amount'>(
    coupon?.discountType ?? 'percent',
  );
  const [discountValue, setDiscountValue] = useState(coupon?.discountValue ?? '');
  // A new preset starts as "whole order": that is what a till discount almost
  // always is. A new code starts on selected products, as it always has.
  const [appliesToAll, setAppliesToAll] = useState(coupon?.appliesToAll ?? isPreset);
  const [requiresManagerPin, setRequiresManagerPin] = useState(coupon?.requiresManagerPin ?? false);
  const [oneTime, setOneTime] = useState(coupon?.oneTime ?? false);
  const [maxUses, setMaxUses] = useState(coupon?.maxUses != null ? String(coupon.maxUses) : '');
  const [perUserLimit, setPerUserLimit] = useState(
    coupon?.perUserLimit != null ? String(coupon.perUserLimit) : '',
  );
  const [validFrom, setValidFrom] = useState(toDateInput(coupon?.validFrom));
  const [validUntil, setValidUntil] = useState(toDateInput(coupon?.validUntil));
  const [active, setActive] = useState(coupon?.active ?? true);
  const [productIds, setProductIds] = useState<string[]>(coupon?.productIds ?? []);
  const [search, setSearch] = useState('');

  // Candidates come from the SERVER (FTS + trigram, top 20 by relevance). The old
  // shape — one `pageSize: 200` catalog page filtered in the browser — made every
  // product past that page unreachable while looking like it worked.
  const { results, isLoading: searching } = useProductSearch({
    term: search,
    filter: { activeOnly: true },
  });
  // The eligible-products list used to BE the catalog, so a product selected but
  // absent from that page had no checkbox to untick — invisible and unremovable
  // while still saved. The selection is now rendered on its own, resolved by id
  // (archived included), and the list below is purely "search to add".
  const { products: selectedProducts } = useProductsByIds(productIds);

  // A manager PIN can only be asked for at the till.
  const pinAllowed = isPreset || channel === 'pos';

  const save = useMutation({
    mutationFn: () => {
      const payload: CouponInput = {
        discountType,
        discountValue: discountValue.trim(),
        oneTime,
        maxUses: oneTime ? null : maxUses.trim() === '' ? null : Number(maxUses),
        perUserLimit: perUserLimit.trim() === '' ? null : Number(perUserLimit),
        validFrom: validFrom ? new Date(validFrom).toISOString() : null,
        validUntil: validUntil ? new Date(validUntil).toISOString() : null,
        active,
        productIds: appliesToAll ? [] : productIds,
        appliesToAll,
        name: name.trim() || null,
        requiresManagerPin: pinAllowed && requiresManagerPin,
        ...(isPreset ? { posPreset: true } : { channel }),
      };
      if (coupon) return updateCoupon(coupon.id, payload);
      return createCoupon(isPreset ? payload : { ...payload, code: code.trim() });
    },
    meta: { successMessage: isPreset ? 'Preset saved.' : 'Code saved.' },
    onSuccess: onSaved,
  });

  function toggleProduct(id: string) {
    setProductIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  const err = (save.error as { response?: { data?: { error?: string } } } | null)?.response?.data
    ?.error;
  const canSave =
    (isPreset ? name.trim() : code.trim()) &&
    discountValue.trim() &&
    (appliesToAll || productIds.length > 0);

  return (
    <div className="rounded-md border bg-muted/30 p-4 space-y-3">
      <h3 className="text-base font-semibold">
        {coupon ? (isPreset ? 'Edit preset' : 'Edit code') : isPreset ? 'New preset' : 'New code'}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {isPreset ? (
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Name on the button</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Senior citizen"
              maxLength={60}
            />
          </div>
        ) : (
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Code</label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              disabled={!!coupon}
              placeholder="SAVE10"
            />
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Type</label>
            <Select
              value={discountType}
              onValueChange={(v) => setDiscountType(v as 'percent' | 'amount')}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="percent">% off</SelectItem>
                <SelectItem value="amount">Amount off / unit</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              {discountType === 'percent' ? 'Percent (0–100)' : 'Amount'}
            </label>
            <Input
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              placeholder="10"
            />
          </div>
        </div>
        {!isPreset && (
          <>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Where it works</label>
              <Select value={channel} onValueChange={(v) => setChannel(v as CouponChannel)}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ecom">{CHANNEL_LABEL.ecom}</SelectItem>
                  <SelectItem value="pos">{CHANNEL_LABEL.pos}</SelectItem>
                  <SelectItem value="both">{CHANNEL_LABEL.both}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">
                Name on the receipt (optional)
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Blank = the code is printed"
                maxLength={60}
              />
            </div>
          </>
        )}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Max uses (blank = unlimited)</label>
          <Input
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
            disabled={oneTime}
            type="number"
            min={1}
            placeholder={oneTime ? '1 (one-time)' : ''}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">
            Per-member limit (blank = unlimited)
          </label>
          <Input
            value={perUserLimit}
            onChange={(e) => setPerUserLimit(e.target.value)}
            type="number"
            min={1}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Valid from (optional)</label>
          <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Valid until (optional)</label>
          <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
        </div>
      </div>

      <div className="flex flex-wrap gap-4">
        {!isPreset && (
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={oneTime} onCheckedChange={(v) => setOneTime(v === true)} />
            One-time use (single global use)
          </label>
        )}
        {pinAllowed && (
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={requiresManagerPin}
              onCheckedChange={(v) => setRequiresManagerPin(v === true)}
            />
            Needs a manager PIN at the till
          </label>
        )}
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={active} onCheckedChange={(v) => setActive(v === true)} />
          Active
        </label>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-semibold text-muted-foreground">Applies to</label>
        <div className="flex flex-wrap gap-2">
          {(
            [
              [true, 'The whole order'],
              [false, 'Selected products only'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={label}
              type="button"
              onClick={() => setAppliesToAll(value)}
              className={
                'rounded-md border px-3 py-1.5 text-sm ' +
                (appliesToAll === value
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'bg-background hover:bg-accent')
              }
            >
              {label}
            </button>
          ))}
        </div>
        {appliesToAll ? (
          <p className="text-xs text-muted-foreground">
            Every line of the order gets the discount.
          </p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">
              Only these lines get the discount ({productIds.length} selected).
            </p>
            {selectedProducts.length > 0 && (
              <div className="flex flex-wrap gap-1.5 rounded border bg-muted/30 p-2">
                {selectedProducts.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => toggleProduct(p.id)}
                    title="Remove"
                    className="flex max-w-full items-center gap-1 rounded-full border bg-background px-2 py-0.5 text-xs hover:bg-muted"
                  >
                    <span className="truncate">{p.name}</span>
                    <span className="font-mono text-muted-foreground">{p.sku}</span>
                    {!p.active && <span className="text-muted-foreground">· archived</span>}
                    <span aria-hidden>×</span>
                  </button>
                ))}
              </div>
            )}
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products…"
              className="max-w-xs"
            />
            <div className="max-h-56 overflow-y-auto rounded border bg-background p-2 space-y-1">
              {results.map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-sm py-0.5">
                  <Checkbox
                    checked={productIds.includes(p.id)}
                    onCheckedChange={() => toggleProduct(p.id)}
                  />
                  <span className="truncate">{p.name}</span>
                  <span className="text-xs text-muted-foreground font-mono">{p.sku}</span>
                </label>
              ))}
              {results.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  {searching ? 'Searching…' : 'No products match.'}
                </p>
              )}
            </div>
          </>
        )}
      </div>

      {err && <p className="text-sm text-destructive">{ERROR_COPY[err] ?? err}</p>}
      <div className="flex gap-2 pt-1">
        <Button disabled={!canSave || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : coupon ? 'Save' : 'Create'}
        </Button>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** What the server's refusals mean, in the admin's words. */
const ERROR_COPY: Record<string, string> = {
  coupon_preset_needs_name: 'A preset needs a name for its button.',
  coupon_needs_products: 'Pick at least one product, or apply it to the whole order.',
  coupon_needs_code: 'Type a code.',
  coupon_pin_is_pos_only: 'Only a discount that works at the till alone can need a manager PIN.',
  coupon_till_fields_house_only: 'A merchant or creator code works at the online store only.',
};

/** ISO timestamp → yyyy-mm-dd for a <input type="date">. */
function toDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 10);
}
